import React, { useState } from 'react';
import { Search, Filter, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, Edit3, Trash2 } from 'lucide-react';
import { COLORS } from '../../utils/constants';

const C = COLORS;

export const DataTable = ({
  columns,
  data,
  onView,
  onEdit,
  onDelete,
  expandRow,
  page,
  totalPages,
  total,
  itemsPerPage,
  onItemsPerPageChange,
  onPageChange,
  searchPlaceholder,
  filterOptions,
  onSearch,
  onFilter,
  filters,
  extraFilters,
  hideToolbar,
}) => {
  const [search, setSearch] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [activeFilterIdx, setActiveFilterIdx] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const handleSearch = (val) => {
    setSearch(val);
    onSearch?.(val);
  };

  const getPaginationItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (page <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }
    if (page >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', page - 1, page, page + 1, '...', totalPages];
  };

  const isAdvancedPagination = itemsPerPage !== undefined || total !== undefined;
  const totalCount = total !== undefined ? total : data.length;
  const currentLimit = itemsPerPage || 10;

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, overflow: 'hidden' }}>
      {/* Search & Filter Bar */}
      {!hideToolbar && (
        <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.g200}`, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.g400 }} />
            <input value={search} onChange={e => handleSearch(e.target.value)} placeholder={searchPlaceholder || 'Search...'}
              style={{ width: '100%', padding: '10px 12px 10px 38px', border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, outline: 'none', background: C.g50, boxSizing: 'border-box' }} />
          </div>
          {extraFilters}
          {filterOptions && !filters && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowFilter(!showFilter)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: `1px solid ${C.g200}`, borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 13, color: C.g600 }}>
                <Filter size={14} /> Filter <ChevronDown size={14} />
              </button>
              {showFilter && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 12, padding: 8, minWidth: 160, boxShadow: '0 8px 30px rgba(0,0,0,0.08)', zIndex: 20 }}>
                  {filterOptions.map((f, i) => (
                    <button key={i} onClick={() => { onFilter?.(f); setShowFilter(false); }}
                      style={{ display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, color: C.g700, borderRadius: 8 }}
                      onMouseEnter={e => e.target.style.background = C.g100} onMouseLeave={e => e.target.style.background = 'none'}>
                      {f.label || f}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {filters && filters.map((filterGroup, idx) => (
            <div key={idx} style={{ position: 'relative' }}>
              <button onClick={() => setActiveFilterIdx(activeFilterIdx === idx ? null : idx)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: `1px solid ${C.g200}`, borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 13, color: C.g600 }}>
                <Filter size={14} /> {filterGroup.label}: {filterGroup.value || 'All'} <ChevronDown size={14} />
              </button>
              {activeFilterIdx === idx && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 12, padding: 8, minWidth: 160, boxShadow: '0 8px 30px rgba(0,0,0,0.08)', zIndex: 20 }}>
                  {filterGroup.options.map((f, i) => (
                    <button key={i} onClick={() => { filterGroup.onChange?.(f); setActiveFilterIdx(null); }}
                      style={{ display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, color: filterGroup.value === (f.label || f) ? C.primary : C.g700, fontWeight: filterGroup.value === (f.label || f) ? 600 : 400, borderRadius: 8 }}
                      onMouseEnter={e => e.target.style.background = C.g100} onMouseLeave={e => e.target.style.background = 'none'}>
                      {f.label || f}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.g200}` }}>
              {columns.map(col => (
                <th key={col.key} style={{ padding: '14px 24px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: col.wrap ? 'normal' : 'nowrap', ...col.headerStyle }}>
                  {col.label}
                </th>
              ))}
              {(onView || onEdit || onDelete) && (
                <th style={{ padding: '14px 24px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', letterSpacing: 0.5 }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={columns.length + 1} style={{ padding: 40, textAlign: 'center', color: C.g400 }}>No records found</td></tr>
            ) : data.map((row, idx) => (
              <React.Fragment key={row.id || idx}>
                <tr style={{ borderBottom: `1px solid ${C.g100}`, cursor: expandRow ? 'pointer' : 'default', verticalAlign: 'top' }}
                  onClick={() => expandRow && setExpanded(expanded === idx ? null : idx)}
                  onMouseEnter={e => e.currentTarget.style.background = C.g50}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {columns.map(col => (
                    <td
                      key={col.key}
                      style={{
                        padding: '14px 24px',
                        whiteSpace: col.wrap || col.style?.whiteSpace ? (col.style?.whiteSpace || 'normal') : 'nowrap',
                        verticalAlign: col.verticalAlign || (col.wrap ? 'top' : 'middle'),
                        wordBreak: col.wrap ? 'break-word' : undefined,
                        overflowWrap: col.wrap ? 'anywhere' : undefined,
                        ...col.style
                      }}
                    >
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                  {(onView || onEdit || onDelete) && (
                    <td style={{ padding: '14px 24px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {onView && <button onClick={e => { e.stopPropagation(); onView(row); }} style={{ ...iconBtn, color: C.info }}><Eye size={15} /></button>}
                        {onEdit && <button onClick={e => { e.stopPropagation(); onEdit(row); }} style={{ ...iconBtn, color: C.primary }}><Edit3 size={15} /></button>}
                        {onDelete && <button onClick={e => { e.stopPropagation(); onDelete(row); }} style={{ ...iconBtn, color: C.danger }}><Trash2 size={15} /></button>}
                      </div>
                    </td>
                  )}
                </tr>
                {expandRow && expanded === idx && (
                  <tr><td colSpan={columns.length + 1} style={{ padding: '16px 24px 16px 56px', background: C.g50, borderBottom: `1px solid ${C.g200}` }}>
                    {expandRow(row)}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {isAdvancedPagination ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          borderTop: `1px solid ${C.g200}`,
          background: '#FAFAFA',
          flexWrap: 'wrap',
          gap: 16
        }}>
          {/* Records per page & Showing entries */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {onItemsPerPageChange && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: C.g600, fontWeight: 500 }}>Records per page:</span>
                <select
                  value={currentLimit}
                  onChange={e => {
                    onItemsPerPageChange(Number(e.target.value));
                    onPageChange?.(1);
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: `1px solid ${C.g200}`,
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.g800,
                    background: '#fff',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            )}
            <span style={{ fontSize: 13, color: C.g500 }}>
              Showing {data.length === 0 ? 0 : (page - 1) * currentLimit + 1} to {Math.min(page * currentLimit, totalCount)} of {totalCount} entries
            </span>
          </div>

          {/* Pagination Navigation */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* First Page button */}
              <button
                type="button"
                onClick={() => onPageChange?.(1)}
                disabled={page === 1}
                title="First page"
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: page === 1 ? C.g300 : C.g700,
                  cursor: page === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronsLeft size={16} />
              </button>

              {/* Previous button */}
              <button
                type="button"
                onClick={() => onPageChange?.(Math.max(1, page - 1))}
                disabled={page === 1}
                title="Previous page"
                style={{
                  padding: '0 10px',
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: page === 1 ? C.g300 : C.g700,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: page === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronLeft size={16} /> Prev
              </button>

              {/* Page numbers with ellipsis */}
              {getPaginationItems().map((item, index) => {
                if (item === '...') {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      style={{
                        width: 28,
                        textAlign: 'center',
                        color: C.g400,
                        fontSize: 13,
                        fontWeight: 600,
                        userSelect: 'none'
                      }}
                    >
                      …
                    </span>
                  );
                }

                const isCurrent = item === page;
                return (
                  <button
                    key={`page-${item}`}
                    type="button"
                    onClick={() => onPageChange?.(item)}
                    style={{
                      minWidth: 34,
                      height: 34,
                      padding: '0 8px',
                      borderRadius: 8,
                      border: `1px solid ${isCurrent ? C.primary : C.g200}`,
                      background: isCurrent ? C.primary : '#fff',
                      color: isCurrent ? '#fff' : C.g700,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {item}
                  </button>
                );
              })}

              {/* Next button */}
              <button
                type="button"
                onClick={() => onPageChange?.(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                title="Next page"
                style={{
                  padding: '0 10px',
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: page === totalPages ? C.g300 : C.g700,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: page === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next <ChevronRight size={16} />
              </button>

              {/* Last Page button */}
              <button
                type="button"
                onClick={() => onPageChange?.(totalPages)}
                disabled={page === totalPages}
                title="Last page"
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: page === totalPages ? C.g300 : C.g700,
                  cursor: page === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          )}
        </div>
      ) : (
        totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderTop: `1px solid ${C.g200}` }}>
            <span style={{ fontSize: 13, color: C.g500 }}>Page {page} of {totalPages}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} style={{ ...pgBtn, opacity: page <= 1 ? 0.4 : 1 }}><ChevronLeft size={16} /></button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => onPageChange(p)} style={{ ...pgBtn, background: p === page ? C.primary : 'transparent', color: p === page ? '#fff' : C.g600 }}>{p}</button>
              ))}
              <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} style={{ ...pgBtn, opacity: page >= totalPages ? 0.4 : 1 }}><ChevronRight size={16} /></button>
            </div>
          </div>
        )
      )}
    </div>
  );
};

const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const pgBtn = { border: `1px solid ${C.g200}`, borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 13, background: 'transparent', color: C.g600 };

export default DataTable;
